import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';

const makeIcon = (color) => L.divIcon({
  className: 'custom-marker',
  html: `<div style="background:${color};width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 0 0 2px ${color}"></div>`,
  iconSize: [18, 18], iconAnchor: [9, 9],
});

const truckIcon = L.divIcon({
  className: 'truck-marker',
  html: `<div style="background:#F97316;width:28px;height:28px;border-radius:8px;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);font-size:15px">🚚</div>`,
  iconSize: [28, 28], iconAnchor: [14, 14],
});

// Module-level constants so child map components receive stable references
// (inline objects/arrays would create new references on every render).
const DEPOT_ICON = makeIcon('#0F4C81');
const DEST_ICON = makeIcon('#15803D');
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION = '&copy; OpenStreetMap';
const MAP_STYLE = { height: '100%', width: '100%' };
const ROUTE_PATH_OPTIONS = { color: '#0F4C81', weight: 3, opacity: 0.8 };
const FALLBACK_CENTER = { lat: 28.5563, lng: 77.3253 }; // depot: Sector 95, Noida

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    const valid = points.filter((p) => p && p.lat && p.lng);
    if (valid.length === 1) { map.setView([valid[0].lat, valid[0].lng], 13); }
    else if (valid.length > 1) {
      map.fitBounds(valid.map((p) => [p.lat, p.lng]), { padding: [50, 50], maxZoom: 14 });
    }
  }, [points, map]);
  return null;
};

export const MapView = ({ driver, destination, origin, route, markers, height = '340px', center }) => {
  const c = center || destination || origin || FALLBACK_CENTER;
  const mapCenter = useMemo(() => [c.lat, c.lng], [c.lat, c.lng]);
  const pts = useMemo(() => [driver, destination, origin, ...(markers || [])].filter(Boolean),
    [driver, destination, origin, markers]);
  const originPos = useMemo(() => (origin ? [origin.lat, origin.lng] : null), [origin]);
  const destPos = useMemo(() => (destination ? [destination.lat, destination.lng] : null), [destination]);
  const driverPos = useMemo(() => (driver ? [driver.lat, driver.lng] : null), [driver]);
  const routePositions = useMemo(() => (route && route.length > 1 ? route.map((p) => [p.lat, p.lng]) : null), [route]);
  const wrapStyle = useMemo(() => ({ height }), [height]);
  return (
    <div className="rounded-xl border border-border overflow-hidden bg-card" style={wrapStyle} data-testid="map-view">
      <MapContainer center={mapCenter} zoom={12} style={MAP_STYLE} scrollWheelZoom={false}>
        <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
        {originPos && <Marker position={originPos} icon={DEPOT_ICON}><Popup>Depot</Popup></Marker>}
        {destPos && <Marker position={destPos} icon={DEST_ICON}><Popup>Delivery Location</Popup></Marker>}
        {driverPos && <Marker position={driverPos} icon={truckIcon}><Popup>Driver</Popup></Marker>}
        {(markers || []).map((m, i) => (
          <Marker key={`${m.lat}-${m.lng}-${m.label || i}`} position={[m.lat, m.lng]} icon={truckIcon} data-testid="admin-live-map-driver-marker">
            <Popup>{m.label}</Popup>
          </Marker>
        ))}
        {routePositions && <Polyline positions={routePositions} pathOptions={ROUTE_PATH_OPTIONS} />}
        <FitBounds points={pts} />
      </MapContainer>
    </div>
  );
};
